// Entity graph workbench.
//
// Four tabs, in the order they matter:
//
//   Candidates  — names extraction could not resolve. This is the queue that
//                 keeps the graph from fragmenting: promoting "Africa Corps"
//                 once re-points every article that mentioned it. Similar
//                 existing entities are shown first so a tired reviewer sees
//                 "United States" before reaching for "create new" on
//                 "United States of America".
//   Entities    — the canonical nodes and their aliases.
//   Predicates  — READ ONLY. The vocabulary is PLOVER, defined in
//                 apps/api/src/services/news/vocabulary.ts. Adding a category
//                 changes the shape of every future extraction, so it is a
//                 reviewed code change, not a form.
//   Proposals   — relations the model wanted and could not express. Evidence
//                 for the next revision of the vocabulary.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import { Input, Label, Select, Textarea } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { Spinner } from "../../components/ui/Spinner";
import { api, ApiError } from "../../lib/api";

type Tab = "candidates" | "entities" | "predicates" | "proposals";

interface EntityRef {
  id: string;
  name: string;
  entity_type: string;
}

interface Candidate {
  id: string;
  raw_name: string;
  name_norm: string;
  suggested_type: string | null;
  suggested_description: string | null;
  mention_count: number;
  article_count: number;
  status: string;
  first_article_id: string | null;
  last_seen_at: string;
  similar_entities: EntityRef[];
}

interface Entity {
  id: string;
  name: string;
  entity_type: string;
  iso_code: string | null;
  description: string | null;
  mention_count: number;
  /** Canonical identifying image. One per entity, reused everywhere it appears. */
  image_url: string | null;
  image_credit?: string | null;
  image_license?: string | null;
}

interface PredicateDef {
  key: string;
  label: string;
  inverse_label: string | null;
  description: string;
  predicate_class: "event" | "structural";
  modes: string[];
  subject_types: string[];
  object_types: string[];
  is_active: boolean;
}

interface EntityTypeDef {
  key: string;
  label: string;
  description: string;
}

interface Proposal {
  id: string;
  proposed_key: string;
  relation_phrase: string;
  subject_text: string | null;
  object_text: string | null;
  evidence_quote: string | null;
  occurrence_count: number;
  article_id: string | null;
}

export default function EntityGraphPage() {
  const [tab, setTab] = useState<Tab>("candidates");
  const [pendingCount, setPendingCount] = useState(0);

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.h1}>Entity graph</h1>
          <CardSubtle>
            Extraction proposes; nothing enters the graph until it is approved.
          </CardSubtle>
        </div>
      </div>

      <div style={styles.tabs}>
        <TabBtn
          active={tab === "candidates"}
          onClick={() => setTab("candidates")}
        >
          Candidates
          {pendingCount > 0 ? (
            <span style={styles.badge}>{pendingCount}</span>
          ) : null}
        </TabBtn>
        <TabBtn active={tab === "entities"} onClick={() => setTab("entities")}>
          Entities
        </TabBtn>
        <TabBtn
          active={tab === "predicates"}
          onClick={() => setTab("predicates")}
        >
          Predicates
        </TabBtn>
        <TabBtn
          active={tab === "proposals"}
          onClick={() => setTab("proposals")}
        >
          Proposals
        </TabBtn>
      </div>

      {tab === "candidates" ? (
        <CandidatesTab onCount={setPendingCount} />
      ) : tab === "entities" ? (
        <EntitiesTab />
      ) : tab === "predicates" ? (
        <PredicatesTab />
      ) : (
        <ProposalsTab />
      )}
    </div>
  );
}

// ── Candidates ─────────────────────────────────────────────────────────────

function CandidatesTab({ onCount }: { onCount: (n: number) => void }) {
  const [rows, setRows] = useState<Candidate[]>([]);
  const [types, setTypes] = useState<EntityTypeDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [promoting, setPromoting] = useState<Candidate | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api<{ candidates: Candidate[] }>(
        "/news/editor/graph/candidates?status=pending&limit=100",
      );
      setRows(r.candidates);
      onCount(r.candidates.length);
      setError(null);
    } catch (e: any) {
      setError(e.message ?? "Failed to load candidates");
    } finally {
      setLoading(false);
    }
  }, [onCount]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api<{ entity_types: EntityTypeDef[] }>("/news/editor/graph/predicates")
      .then((r) => setTypes(r.entity_types ?? []))
      .catch(() => setTypes([]));
  }, []);

  async function resolve(
    c: Candidate,
    body: Record<string, unknown>,
  ): Promise<void> {
    setBusy(c.id);
    setError(null);
    try {
      await api(`/news/editor/graph/candidates/${c.id}/resolve`, {
        method: "POST",
        body,
      });
      await load();
    } catch (e: any) {
      setError(
        e instanceof ApiError && e.status === 409
          ? e.message
          : (e.message ?? "Could not resolve that candidate"),
      );
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <Loading />;

  return (
    <>
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      <CardSubtle style={{ marginBottom: 12 }}>
        Names extraction could not match to a canonical entity, most-mentioned
        first. Resolving one re-points every article that used it — you only do
        this once per name. If it is something the graph already knows under a
        different spelling, map onto that instead of creating a second node.
      </CardSubtle>

      {rows.length === 0 ? (
        <Card>
          <CardSubtle>
            Nothing pending. Every extracted name resolved to a known entity.
          </CardSubtle>
        </Card>
      ) : (
        rows.map((c) => (
          <Card key={c.id} style={{ marginBottom: 10 }}>
            <div style={styles.rowBetween}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CardTitle style={{ marginBottom: 2 }}>{c.raw_name}</CardTitle>
                <CardSubtle>
                  {c.suggested_type ?? "type unknown"} · {c.mention_count}{" "}
                  mention{c.mention_count === 1 ? "" : "s"} in {c.article_count}{" "}
                  article{c.article_count === 1 ? "" : "s"}
                  {c.first_article_id ? (
                    <>
                      {" · "}
                      <Link
                        to={`/article/${c.first_article_id}`}
                        style={styles.link}
                      >
                        first seen
                      </Link>
                    </>
                  ) : null}
                </CardSubtle>
                {c.suggested_description ? (
                  <CardSubtle style={{ marginTop: 4 }}>
                    {c.suggested_description}
                  </CardSubtle>
                ) : null}
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <Button
                  size="sm"
                  onClick={() => setPromoting(c)}
                  disabled={busy === c.id}
                  title="Map onto an existing entity, or create it as new"
                >
                  Resolve
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    if (
                      confirm(
                        `Reject "${c.raw_name}"? It will never be queued again, and its proposed mentions are removed.`,
                      )
                    )
                      void resolve(c, { action: "reject" });
                  }}
                  disabled={busy === c.id}
                >
                  Reject
                </Button>
              </div>
            </div>

            {c.similar_entities.length > 0 ? (
              <div style={styles.similarBox}>
                <CardSubtle style={{ marginBottom: 4 }}>
                  Already in the graph — map onto one of these instead of
                  creating a duplicate:
                </CardSubtle>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {c.similar_entities.map((s) => (
                    <button
                      key={s.id}
                      style={styles.similarChip}
                      disabled={busy === c.id}
                      onClick={() =>
                        resolve(c, { action: "alias", entity_id: s.id })
                      }
                      title={`Record "${c.raw_name}" as an alias of ${s.name}`}
                    >
                      {s.name}
                      <span style={{ color: "var(--text-muted)" }}>
                        {" "}
                        {s.entity_type}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </Card>
        ))
      )}

      {promoting ? (
        <PromoteModal
          candidate={promoting}
          types={types}
          onClose={() => setPromoting(null)}
          onSave={async (body) => {
            await resolve(promoting, body);
            setPromoting(null);
          }}
        />
      ) : null}
    </>
  );
}

function PromoteModal({
  candidate,
  types,
  onClose,
  onSave,
}: {
  candidate: Candidate;
  types: EntityTypeDef[];
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => Promise<void>;
}) {
  const [name, setName] = useState(candidate.raw_name);
  const [type, setType] = useState(
    candidate.suggested_type ?? types[0]?.key ?? "organization",
  );
  const [iso, setIso] = useState("");
  const [description, setDescription] = useState(
    candidate.suggested_description ?? "",
  );
  const [saving, setSaving] = useState(false);

  // Mapping onto an existing entity must ALWAYS be reachable here. The chips
  // on the row only render when the suggestion heuristic finds something, and
  // when it finds nothing there was previously no way to map at all — which
  // is precisely how a duplicate node gets created.
  const [mapQuery, setMapQuery] = useState("");
  const [mapResults, setMapResults] = useState<EntityRef[]>([]);

  useEffect(() => {
    if (!mapQuery.trim()) {
      setMapResults([]);
      return;
    }
    const t = setTimeout(() => {
      api<{ entities: EntityRef[] }>(
        `/news/editor/graph/entities?limit=6&q=${encodeURIComponent(mapQuery.trim())}`,
      )
        .then((r) => setMapResults(r.entities))
        .catch(() => setMapResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [mapQuery]);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Resolve “${candidate.raw_name}”`}
      width={560}
    >
      <div style={styles.mapBox}>
        <Label style={{ marginBottom: 4 }}>
          Already in the graph? Map onto it instead
        </Label>
        <CardSubtle style={{ marginBottom: 6 }}>
          A second node for something that already exists is what splits a
          graph. Search first — a match here records an alias rather than a
          duplicate.
        </CardSubtle>
        <Input
          value={mapQuery}
          onChange={(e) => setMapQuery(e.target.value)}
          placeholder="search names and aliases…"
        />
        <div
          style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}
        >
          {mapResults.map((r) => (
            <button
              key={r.id}
              disabled={saving}
              style={styles.similarChip}
              title={`Record “${candidate.raw_name}” as another name for ${r.name}`}
              onClick={async () => {
                setSaving(true);
                await onSave({ action: "alias", entity_id: r.id });
                setSaving(false);
              }}
            >
              {r.name}
              <span style={{ color: "var(--text-muted)" }}>
                {" "}
                {r.entity_type}
              </span>
            </button>
          ))}
        </div>
        {mapQuery.trim() && mapResults.length === 0 ? (
          <CardSubtle style={{ marginTop: 6 }}>
            No match — create it as new below.
          </CardSubtle>
        ) : null}
      </div>

      <SectionHeading style={{ marginTop: 0 }}>
        Or create it as a new entity
      </SectionHeading>

      <Label>Display name</Label>
      <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      <CardSubtle style={{ marginTop: 4 }}>
        Seen in the text as “{candidate.raw_name}”, which becomes an alias.
      </CardSubtle>

      <Label style={{ marginTop: 12 }}>Type</Label>
      <Select value={type} onChange={(e) => setType(e.target.value)}>
        {types.map((t) => (
          <option key={t.key} value={t.key}>
            {t.label}
          </option>
        ))}
      </Select>
      {types.find((t) => t.key === type) ? (
        <CardSubtle style={{ marginTop: 4 }}>
          {types.find((t) => t.key === type)!.description}
        </CardSubtle>
      ) : null}

      {type === "country" ? (
        <>
          <Label style={{ marginTop: 12 }}>ISO 3166-1 alpha-2</Label>
          <Input
            value={iso}
            onChange={(e) => setIso(e.target.value.toUpperCase())}
            maxLength={2}
            placeholder="e.g. SS"
          />
        </>
      ) : null}

      <Label style={{ marginTop: 12 }}>Description (optional)</Label>
      <Input
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <Button
          disabled={saving || !name.trim()}
          onClick={async () => {
            setSaving(true);
            await onSave({
              action: "promote",
              name: name.trim(),
              entity_type: type,
              iso_code: type === "country" && iso.length === 2 ? iso : null,
              description: description.trim() || null,
            });
            setSaving(false);
          }}
        >
          {saving ? "Promoting…" : "Promote"}
        </Button>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

// ── Entities ───────────────────────────────────────────────────────────────

function EntitiesTab() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Entity | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api<{ entities: Entity[] }>(
        `/news/editor/graph/entities?limit=50${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ""}`,
      )
        .then((r) => setRows(r.entities))
        .catch(() => setRows([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, reloadKey]);

  return (
    <>
      <Card style={{ marginBottom: 12 }}>
        <Label>Search entities and aliases</Label>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="try an alias: usa, drc, saf…"
        />
      </Card>

      {loading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Card>
          <CardSubtle>No matching entities.</CardSubtle>
        </Card>
      ) : (
        rows.map((e) => (
          <Card key={e.id} style={{ marginBottom: 8 }}>
            <div style={styles.rowBetween}>
              <div style={{ display: "flex", gap: 12, minWidth: 0 }}>
                {e.image_url ? (
                  <img src={e.image_url} alt="" style={styles.entityThumb} />
                ) : null}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{e.name}</div>
                  <CardSubtle>
                    {e.entity_type}
                    {e.iso_code ? ` · ${e.iso_code}` : ""} · {e.mention_count}{" "}
                    mention{e.mention_count === 1 ? "" : "s"}
                  </CardSubtle>
                  {e.description ? (
                    <CardSubtle style={{ marginTop: 4 }}>
                      {e.description}
                    </CardSubtle>
                  ) : null}
                </div>
              </div>
              <Button variant="secondary" onClick={() => setEditing(e)}>
                Edit
              </Button>
            </div>
          </Card>
        ))
      )}

      {editing ? (
        <EntityEditModal
          entity={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Editor-maintained attributes of a canonical entity.
 *
 * Narrow on purpose: name and type are not here. Renaming an entity is an
 * identity change and has to go through the alias machinery, or the global
 * UNIQUE (alias_norm) lock stops meaning what it says.
 *
 * The portrait lives here rather than on a context card because the graph
 * already guarantees exactly one of each subject — so one photograph serves
 * every card, row and future entity page, instead of each carrying its own.
 */
function EntityEditModal({
  entity,
  onClose,
  onSaved,
}: {
  entity: Entity;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [description, setDescription] = useState(entity.description ?? "");
  const [imageUrl, setImageUrl] = useState(entity.image_url ?? "");
  const [credit, setCredit] = useState(entity.image_credit ?? "");
  const [license, setLicense] = useState(entity.image_license ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const needsCredit = !!imageUrl.trim() && !credit.trim();

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await api(`/news/editor/graph/entities/${entity.id}`, {
        method: "PATCH",
        body: {
          description: description.trim() || null,
          image_url: imageUrl.trim() || null,
          image_credit: credit.trim() || null,
          image_license: license.trim() || null,
        },
      });
      onSaved();
    } catch (e: any) {
      const d = e?.data?.error;
      setErr(
        typeof d === "string"
          ? d
          : d
            ? Object.entries(d)
                .map(([f, m]) => `${f.replace(/_/g, " ")}: ${(m as string[]).join(", ")}`)
                .join(" · ")
            : (e.message ?? "Could not save"),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={entity.name} width={560}>
      <CardSubtle style={{ marginTop: 0 }}>
        {entity.entity_type}
        {entity.iso_code ? ` · ${entity.iso_code}` : ""}. Name and type are not
        editable here — renaming is an identity change and goes through aliases.
      </CardSubtle>

      <Label style={{ marginTop: 12 }}>Description</Label>
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={3}
        placeholder="One line an editor would want when this name appears in a story."
      />

      <Label style={{ marginTop: 14 }}>Image</Label>
      <Input
        value={imageUrl}
        onChange={(e) => setImageUrl(e.target.value)}
        placeholder="https://…"
      />
      <CardSubtle style={{ marginTop: 4 }}>
        Used by every context card about this subject that has no image of its
        own. Editors supply these — the drafting model is never asked for one.
      </CardSubtle>

      {imageUrl.trim() ? (
        <>
          <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
            <div style={{ flex: 2 }}>
              <Label>Credit — required</Label>
              <Input
                value={credit}
                onChange={(e) => setCredit(e.target.value)}
                placeholder="Reuters / Jane Doe"
              />
            </div>
            <div style={{ flex: 1 }}>
              <Label>Licence</Label>
              <Input
                value={license}
                onChange={(e) => setLicense(e.target.value)}
                placeholder="CC BY-SA 4.0"
              />
            </div>
          </div>
          {needsCredit ? (
            <CardSubtle style={{ color: "var(--danger)", marginTop: 4 }}>
              An image needs a credit. A photograph of a head of state is almost
              always agency material, and an uncredited one is what draws a
              takedown.
            </CardSubtle>
          ) : null}
          <img src={imageUrl} alt="" style={styles.entityPreview} />
        </>
      ) : null}

      {err ? (
        <CardSubtle style={{ color: "var(--danger)", marginTop: 10 }}>
          {err}
        </CardSubtle>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <Button onClick={save} disabled={saving || needsCredit}>
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

// ── Predicates (read only) ─────────────────────────────────────────────────

function PredicatesTab() {
  const [rows, setRows] = useState<PredicateDef[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api<{ predicates: PredicateDef[] }>(
      "/news/editor/graph/predicates?include_inactive=true",
    )
      .then((r) => setRows(r.predicates))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const events = rows.filter((p) => p.predicate_class === "event");
  const structural = rows.filter((p) => p.predicate_class === "structural");

  if (loading) return <Loading />;

  return (
    <>
      <Card style={{ marginBottom: 12 }}>
        <CardTitle>The vocabulary is fixed, and that is the point</CardTitle>
        <CardSubtle>
          Event predicates are{" "}
          <a
            href="https://github.com/openeventdata/PLOVER"
            target="_blank"
            rel="noreferrer"
            style={styles.link}
          >
            PLOVER
          </a>
          , adopted unchanged — CAMEO's successor, and the basis of the POLECAT
          dataset. CAMEO carried several hundred codes; PLOVER cut to 18 because
          finer distinctions could not be coded reliably by two people reading
          the same report. Detail lives in modes, not in more predicates:
          funding, arming and training are all <code>aid</code>.
          <br />
          <br />
          Adding a category changes the shape of every future extraction, so it
          is a reviewed change to{" "}
          <code>apps/api/src/services/news/vocabulary.ts</code> — not a button
          here. Relations the model could not express collect under Proposals.
        </CardSubtle>
      </Card>

      <SectionHeading>
        Events — something that happened ({events.length})
      </SectionHeading>
      {events.map((p) => (
        <PredicateRow key={p.key} p={p} />
      ))}

      <SectionHeading style={{ marginTop: 16 }}>
        Standing facts — ours, not PLOVER ({structural.length})
      </SectionHeading>
      {structural.map((p) => (
        <PredicateRow key={p.key} p={p} />
      ))}
    </>
  );
}

function PredicateRow({ p }: { p: PredicateDef }) {
  return (
    <Card
      style={{ marginBottom: 6, padding: 12, opacity: p.is_active ? 1 : 0.5 }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <code style={styles.code}>{p.key}</code>
        <span style={{ fontWeight: 600 }}>{p.label}</span>
        {p.inverse_label ? <CardSubtle>↔ {p.inverse_label}</CardSubtle> : null}
        {!p.is_active ? <span style={styles.badgeMuted}>disabled</span> : null}
      </div>
      <CardSubtle style={{ marginTop: 4 }}>{p.description}</CardSubtle>
      {p.modes.length > 0 ? (
        <div
          style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}
        >
          {p.modes.map((m) => (
            <span key={m} style={styles.modeChip}>
              {m}
            </span>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

// ── Proposals ──────────────────────────────────────────────────────────────

function ProposalsTab() {
  const [rows, setRows] = useState<Proposal[]>([]);
  const [predicates, setPredicates] = useState<PredicateDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api<{ proposals: Proposal[] }>(
        "/news/editor/graph/predicate-proposals",
      );
      setRows(r.proposals);
    } catch (e: any) {
      setError(e.message ?? "Failed to load proposals");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api<{ predicates: PredicateDef[] }>("/news/editor/graph/predicates")
      .then((r) => setPredicates(r.predicates))
      .catch(() => setPredicates([]));
  }, []);

  async function resolve(id: string, body: Record<string, unknown>) {
    setBusy(id);
    try {
      await api(`/news/editor/graph/predicate-proposals/${id}/resolve`, {
        method: "POST",
        body,
      });
      await load();
    } catch (e: any) {
      setError(e.message ?? "Could not resolve that proposal");
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <Loading />;

  return (
    <>
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      <CardSubtle style={{ marginBottom: 12 }}>
        Relations the model found real but could not express in the vocabulary.
        These never reach the graph. A recurring one is the argument for adding
        a predicate in the next revision — map it onto an existing category, or
        dismiss it.
      </CardSubtle>

      {rows.length === 0 ? (
        <Card>
          <CardSubtle>
            Nothing pending. Everything extracted so far fitted the vocabulary.
          </CardSubtle>
        </Card>
      ) : (
        rows.map((p) => (
          <Card key={p.id} style={{ marginBottom: 10 }}>
            <div style={styles.rowBetween}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>
                  {p.subject_text ?? "?"}{" "}
                  <code style={styles.code}>{p.proposed_key}</code>{" "}
                  {p.object_text ?? "?"}
                </div>
                <CardSubtle>
                  “{p.relation_phrase}” · seen {p.occurrence_count}×
                  {p.article_id ? (
                    <>
                      {" · "}
                      <Link to={`/article/${p.article_id}`} style={styles.link}>
                        article
                      </Link>
                    </>
                  ) : null}
                </CardSubtle>
                {p.evidence_quote ? (
                  <div style={styles.quote}>“{p.evidence_quote}”</div>
                ) : null}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <Select
                defaultValue=""
                disabled={busy === p.id}
                onChange={(e) => {
                  if (e.target.value)
                    void resolve(p.id, {
                      action: "map",
                      predicate_key: e.target.value,
                    });
                }}
                style={{ maxWidth: 280 }}
              >
                <option value="">Map onto an existing predicate…</option>
                {predicates.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.key} — {d.label}
                  </option>
                ))}
              </Select>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy === p.id}
                onClick={() => void resolve(p.id, { action: "reject" })}
              >
                Dismiss
              </Button>
            </div>
          </Card>
        ))
      )}
    </>
  );
}

// ── local primitives ───────────────────────────────────────────────────────

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        ...styles.tabBtn,
        color: active ? "var(--accent)" : "var(--text-secondary)",
        borderBottomColor: active ? "var(--accent)" : "transparent",
      }}
    >
      {children}
    </button>
  );
}

function SectionHeading({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return <div style={{ ...styles.sectionHeading, ...style }}>{children}</div>;
}

function Loading() {
  return (
    <div style={{ padding: 24, textAlign: "center" }}>
      <Spinner size={18} />
    </div>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <Card style={{ marginBottom: 12 }}>
      <CardSubtle style={{ color: "var(--danger)" }}>{children}</CardSubtle>
    </Card>
  );
}

const styles: Record<string, React.CSSProperties> = {
  entityThumb: {
    width: 48,
    height: 48,
    objectFit: "cover",
    borderRadius: "var(--radius-sm)",
    border: "1px solid var(--border)",
    flexShrink: 0,
  },
  entityPreview: {
    display: "block",
    marginTop: 10,
    width: 120,
    height: 120,
    objectFit: "cover",
    borderRadius: "var(--radius-sm)",
    border: "1px solid var(--border)",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  h1: { fontSize: 22, fontWeight: 700, margin: 0, marginBottom: 4 },
  tabs: {
    display: "flex",
    gap: 4,
    borderBottom: "1px solid var(--border)",
    marginBottom: 16,
  },
  tabBtn: {
    background: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  badge: {
    background: "var(--accent)",
    color: "#0e0e0f",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 700,
    padding: "1px 6px",
  },
  badgeMuted: {
    background: "var(--surface-hover)",
    color: "var(--text-muted)",
    borderRadius: 3,
    fontSize: 10,
    fontWeight: 700,
    padding: "1px 5px",
    textTransform: "uppercase",
  },
  rowBetween: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  similarBox: {
    marginTop: 10,
    paddingTop: 10,
    borderTop: "1px solid var(--border)",
  },
  mapBox: {
    background: "var(--surface-hover)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    marginBottom: 14,
    padding: 12,
  },
  similarChip: {
    background: "var(--surface-hover)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    color: "var(--text-primary)",
    cursor: "pointer",
    fontSize: 12,
    padding: "4px 8px",
  },
  code: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
    color: "var(--accent)",
    background: "var(--surface-hover)",
    borderRadius: 3,
    padding: "1px 5px",
  },
  modeChip: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 11,
    color: "var(--text-muted)",
    background: "var(--surface-hover)",
    borderRadius: 3,
    padding: "1px 5px",
  },
  quote: {
    fontSize: 12,
    fontStyle: "italic",
    color: "var(--text-muted)",
    borderLeft: "2px solid var(--border)",
    paddingLeft: 8,
    marginTop: 6,
  },
  sectionHeading: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: "var(--text-secondary)",
    margin: "12px 0 8px",
  },
  link: { color: "var(--accent)" },
};
